// FacilityDashboard.jsx
import React, { useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
} from "recharts";
import {
    CalendarRange,
    CalendarCheck,
    CheckCircle2,
    XCircle,
    IndianRupee,
    Download,
    TrendingUp,
    Users,
    Cpu,
    MessageCircle,
    Star,
    Bell,
    Search,
    Filter,
    ChevronDown,
    ChevronRight,
    Plus,
    Edit,
    Trash2,
    Eye,
    Clock,
    Truck,
    Package,
    AlertCircle,
    Check,
    ArrowUp,
    ArrowDown,
    MoreVertical,
    Settings,
    LogOut,
    User,
    Building2,
    MapPin,
    Calendar,
    Mail,
    Phone,
    Globe,
    Clock as ClockIcon,
    Award,
    Zap,
    Shield,
    Sparkles
} from "lucide-react";
import "./Repportsanylisis.css";

// ========== SAMPLE DATA ==========
const generateDailyData = (days = 60) => {
    const rows = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const seed = days - i;
        const closed = Math.floor(Math.random() * 10) + 2;
        const active = Math.floor(Math.random() * 8) + 1;
        const cancelled = Math.floor(Math.random() * 3);
        const avgPrice = Math.floor(Math.random() * 1000) + 1200;
        rows.push({
            date: d.toISOString().slice(0, 10),
            label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
            active,
            closed,
            cancelled,
            revenue: closed * avgPrice,
        });
    }
    return rows;
};

const DAILY_DATA = generateDailyData(60);

const STATUS_COLORS = {
    Active: "#4f46e5",
    Closed: "#10b981",
    Cancelled: "#f43f5e",
    "Awaiting Buyer": "#4f46e5",
    "In Production": "#3b82f6",
    Shipped: "#8b5cf6",
    Delivered: "#10b981",
};

const QUICK_RANGES = [
    { label: "7D", days: 7 },
    { label: "30D", days: 30 },
    { label: "60D", days: 60 },
];

// Recent Orders Data
const recentOrders = [
    {
        id: "APIC-ORD-00081",
        name: "Smart IoT Water Sensor",
        buyer: "Apex Robotics Pvt Ltd",
        amount: 178000,
        status: "Awaiting Buyer",
        date: "20 Jul",
        category: "IoT / PCB"
    },
    {
        id: "APIC-ORD-00080",
        name: "Precision Steel Brackets",
        buyer: "Sharma Auto Components",
        amount: 62000,
        status: "In Production",
        date: "18 Jul",
        category: "Industrial"
    },
    {
        id: "APIC-ORD-00079",
        name: "ABS Enclosure Set (Grey)",
        buyer: "PrintForm Buyers Co.",
        amount: 41500,
        status: "Shipped",
        date: "15 Jul",
        category: "3D Printing"
    },
    {
        id: "APIC-ORD-00076",
        name: "Custom Wiring Harness",
        buyer: "Anand Mobility Ltd",
        amount: 95000,
        status: "Delivered",
        date: "05 Jul",
        category: "Electrical"
    }
];

// Machines Data
const machines = [
    { id: "M-01", name: "CNC Milling Machine — HAAS VF2", status: "Running", utilization: 92 },
    { id: "M-02", name: "SMT Pick & Place Line", status: "Running", utilization: 78 },
    { id: "M-03", name: "Reflow Oven — 8 Zone", status: "Idle", utilization: 34 },
    { id: "M-04", name: "Laser Cutting Unit", status: "Maintenance", utilization: 0 },
    { id: "M-05", name: "Injection Moulding Press", status: "Running", utilization: 65 },
    { id: "M-06", name: "3-Axis Robotic Welder", status: "Idle", utilization: 12 }
];

// Chats Data
const chats = [
    { name: "Apex Robotics Pvt Ltd", initial: "AR", time: "10:42 AM", message: "Can you confirm the delivery date for the sensor batch?", unread: true },
    { name: "Sharma Auto Components", initial: "SA", time: "9:15 AM", message: "Attaching the revised drawing for the brackets.", unread: true },
    { name: "Anand Mobility Ltd", initial: "AM", time: "Yesterday", message: "Payment has been released. Thank you!", unread: false },
    { name: "Metro Industrial Buyers", initial: "MI", time: "Yesterday", message: "What's your MOQ for aluminium heat sinks?", unread: false }
];

// Machine Catalog Data
const machineCatalog = [
    { id: "M-3DP-01", name: "Industrial FDM 3D Printer", category: "3D Printing", centre: "APIC Visakhapatnam", make: "Stratasys F770", rate: 850, status: "Available" },
    { id: "M-CNC-05", name: "5-Axis CNC Machining Centre", category: "CNC", centre: "APIC Vijayawada", make: "DMG Mori DMU 50", rate: 2400, status: "Running" },
    { id: "M-LSR-02", name: "Fiber Laser Cutter 3kW", category: "Laser Cutting", centre: "APIC Tirupati", make: "Trumpf TruLaser 3030", rate: 1600, status: "Available" },
    { id: "M-PCB-03", name: "PCB Prototyping Machine", category: "PCB", centre: "APIC Visakhapatnam", make: "LPKF ProtoMat S104", rate: 1100, status: "Maintenance" },
    { id: "M-IMM-01", name: "Injection Moulding 120T", category: "Moulding", centre: "APIC Kurnool", make: "Arburg Allrounder 470", rate: 1900, status: "Booked" },
    { id: "M-ROB-04", name: "6-Axis Industrial Robotic Arm", category: "Robotics", centre: "APIC Vijayawada", make: "KUKA KR 10 R1420", rate: 1400, status: "Available" }
];

// Pipeline Data
const pipelineData = [
    { stage: "Sourcing", count: 4, amount: 1195000 },
    { stage: "Enquiry", count: 2, amount: 275000 },
    { stage: "Quote", count: 1, amount: 920000 },
    { stage: "Approval", count: 1, amount: 920000 },
    { stage: "PO", count: 1, amount: 920000 },
    { stage: "Execution", count: 1, amount: 920000 },
    { stage: "Payment", count: 2, amount: 920000 }
];

// Active Orders
const activeOrders = [
    { id: "APIC-ORD-00081", name: "Smart IoT Water Sensor", category: "IoT / PCB", vendor: "Veltrix Electronics Pvt Ltd", rating: 4.8, location: "Pune, MH", amount: 178000, units: "250 units", delivery: "30 Aug 2026", status: "Awaiting Your Acceptance" },
    { id: "APIC-ORD-00080", name: "Precision Steel Brackets", category: "Industrial", vendor: "Sharma Fabrication Works", rating: 4.5, location: "Ludhiana, PB", amount: 62000, units: "600 pcs", delivery: "10 Aug 2026", status: "Order Placed" },
    { id: "APIC-ORD-00079", name: "ABS Enclosure Set (Grey)", category: "3D Printing", vendor: "PrintForm Industries", rating: 4.2, location: "Coimbatore, TN", amount: 41500, units: "300 units", delivery: "28 Jul 2026", status: "In Production" },
    { id: "APIC-ORD-00076", name: "Custom Wiring Harness", category: "Electrical", vendor: "Anand Cable Solutions", rating: 4.6, location: "Nashik, MH", amount: 95000, units: "150 sets", delivery: "18 Jul 2026", status: "Shipped" }
];

const formatINR = (n) => `₹${n.toLocaleString("en-IN")}`;

// ========== STAT CARD ==========
function StatCard({ icon: Icon, label, value, accent, sub, trend, trendValue }) {
    return (
        <div className="fd-stat-card">
            <div className="fd-stat-card-header">
                <p className="fd-stat-card-label">{label}</p>
                <div className={`fd-stat-card-icon ${accent.bg}`}>
                    <Icon className={`h-4 w-4 ${accent.text}`} />
                </div>
            </div>
            <p className="fd-stat-card-value">{value}</p>
            {sub && <p className="fd-stat-card-sub">{sub}</p>}
            {trend && (
                <div className={`fd-stat-card-trend ${trend === 'up' ? 'up' : 'down'}`}>
                    {trend === 'up' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    <span>{trendValue}</span>
                </div>
            )}
        </div>
    );
}

// ========== MAIN COMPONENT ==========
export default function FacilityDashboard() {
    const [startDate, setStartDate] = useState(DAILY_DATA[DAILY_DATA.length - 30].date);
    const [endDate, setEndDate] = useState(DAILY_DATA[DAILY_DATA.length - 1].date);
    const [searchOrder, setSearchOrder] = useState('');
    const [searchMachine, setSearchMachine] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All categories');
    const [activeTab, setActiveTab] = useState('overview');

    const maxDate = DAILY_DATA[DAILY_DATA.length - 1].date;

    const applyQuickRange = (days) => {
        setStartDate(DAILY_DATA[Math.max(0, DAILY_DATA.length - days)].date);
        setEndDate(maxDate);
    };

    const filtered = useMemo(
        () => DAILY_DATA.filter((r) => r.date >= startDate && r.date <= endDate),
        [startDate, endDate]
    );

    const totals = useMemo(() => {
        const active = filtered.reduce((s, r) => s + r.active, 0);
        const closed = filtered.reduce((s, r) => s + r.closed, 0);
        const cancelled = filtered.reduce((s, r) => s + r.cancelled, 0);
        const revenue = filtered.reduce((s, r) => s + r.revenue, 0);
        return { active, closed, cancelled, revenue, total: active + closed + cancelled };
    }, [filtered]);

    const pieData = [
        { name: "Active", value: totals.active },
        { name: "Closed", value: totals.closed },
        { name: "Cancelled", value: totals.cancelled },
    ];

    const statusColors = {
        "Awaiting Buyer": "#4f46e5",
        "In Production": "#3b82f6",
        Shipped: "#8b5cf6",
        Delivered: "#10b981",
        "Awaiting Your Acceptance": "#4f46e5",
        "Order Placed": "#3b82f6"
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'Awaiting Buyer':
            case 'Awaiting Your Acceptance':
                return <Clock className="h-4 w-4" />;
            case 'In Production':
                return <Cpu className="h-4 w-4" />;
            case 'Shipped':
                return <Truck className="h-4 w-4" />;
            case 'Delivered':
                return <Check className="h-4 w-4" />;
            case 'Order Placed':
                return <Package className="h-4 w-4" />;
            default:
                return <AlertCircle className="h-4 w-4" />;
        }
    };

    const getMachineStatusColor = (status) => {
        switch(status) {
            case 'Running': return '#10b981';
            case 'Available': return '#10b981';
            case 'Idle': return '#4f46e5';
            case 'Maintenance': return '#ef4444';
            case 'Booked': return '#3b82f6';
            default: return '#6b7280';
        }
    };

    return (
        <div className="fd-dashboard">
            <div className="fd-container">
                {/* ========== TOP HEADER ========== */}
                <div className="fd-top-header">
                    <div className="fd-header-left">
                        <div className="fd-user-info">
                            <div className="fd-user-avatar">V</div>
                            <div>
                                <h1 className="fd-user-name">Welcome back, Veltrix</h1>
                                <div className="fd-user-details">
                                    <MapPin className="h-3.5 w-3.5" />
                                    <span>Vizag, AP</span>
                                    <span className="fd-dot">·</span>
                                    <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                                    <span>4.8 (142 reviews)</span>
                                    <span className="fd-dot">·</span>
                                    <span>Member since 2023</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="fd-header-right">
                        <button className="fd-notification-btn">
                            <Bell className="h-5 w-5" />
                            <span className="fd-notification-badge">3</span>
                        </button>
                        <button className="fd-profile-btn">
                            <User className="h-5 w-5" />
                            <ChevronDown className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* ========== QUICK STATS ========== */}
                <div className="fd-quick-stats">
                    <StatCard
                        icon={Cpu}
                        label="Total Machines"
                        value="6"
                        accent={{ bg: "bg-indigo-50", text: "text-indigo-600" }}
                        sub="3 running now"
                    />
                    <StatCard
                        icon={Package}
                        label="Active Orders"
                        value="3"
                        accent={{ bg: "bg-blue-50", text: "text-blue-600" }}
                        sub="2 in production"
                    />
                    <StatCard
                        icon={CheckCircle2}
                        label="Total Orders"
                        value="67"
                        accent={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
                        trend="up"
                        trendValue="12%"
                    />
                    <StatCard
                        icon={IndianRupee}
                        label="Revenue (MTD)"
                        value={formatINR(486200)}
                        accent={{ bg: "bg-violet-50", text: "text-violet-600" }}
                        trend="up"
                        trendValue="8%"
                    />
                    <StatCard
                        icon={MessageCircle}
                        label="Chats"
                        value="4"
                        accent={{ bg: "bg-rose-50", text: "text-rose-600" }}
                        sub="3 unread"
                    />
                    <StatCard
                        icon={Star}
                        label="Vendor Rating"
                        value="4.8"
                        accent={{ bg: "bg-yellow-50", text: "text-yellow-600" }}
                        sub="142 reviews"
                    />
                </div>

                {/* ========== MAIN CONTENT TABS ========== */}
                <div className="fd-tabs">
                    <button 
                        className={`fd-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`fd-tab ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => setActiveTab('orders')}
                    >
                        Orders
                    </button>
                    <button 
                        className={`fd-tab ${activeTab === 'machines' ? 'active' : ''}`}
                        onClick={() => setActiveTab('machines')}
                    >
                        Machines
                    </button>
                    <button 
                        className={`fd-tab ${activeTab === 'pipeline' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pipeline')}
                    >
                        Pipeline
                    </button>
                    <button 
                        className={`fd-tab ${activeTab === 'support' ? 'active' : ''}`}
                        onClick={() => setActiveTab('support')}
                    >
                        Support
                    </button>
                </div>

                {/* ========== OVERVIEW TAB ========== */}
                {activeTab === 'overview' && (
                    <>
                        {/* Reports Section */}
                        <div className="fd-reports-section">
                            <div className="fd-reports-header">
                                <div className="fd-reports-title">
                                    <TrendingUp className="h-5 w-5 text-indigo-600" />
                                    <h2>Reports & Analytics</h2>
                                </div>
                                <button className="fd-export-btn">
                                    <Download className="h-4 w-4" />
                                    Export
                                </button>
                            </div>

                            {/* Date Range */}
                            <div className="fd-date-range">
                                <CalendarRange className="h-4 w-4 text-slate-400" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="fd-date-input"
                                />
                                <span className="text-sm text-slate-400">to</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    min={startDate}
                                    max={maxDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="fd-date-input"
                                />
                                <div className="fd-quick-ranges">
                                    {QUICK_RANGES.map((q) => (
                                        <button
                                            key={q.label}
                                            onClick={() => applyQuickRange(q.days)}
                                            className="fd-quick-range-btn"
                                        >
                                            {q.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Summary Cards */}
                            <div className="fd-summary-cards">
                                <StatCard
                                    icon={CalendarCheck}
                                    label="Active bookings"
                                    value={totals.active}
                                    accent={{ bg: "bg-indigo-50", text: "text-indigo-600" }}
                                />
                                <StatCard
                                    icon={CheckCircle2}
                                    label="Closed bookings"
                                    value={totals.closed}
                                    accent={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
                                />
                                <StatCard
                                    icon={XCircle}
                                    label="Cancelled"
                                    value={totals.cancelled}
                                    accent={{ bg: "bg-rose-50", text: "text-rose-600" }}
                                />
                                <StatCard
                                    icon={IndianRupee}
                                    label="Revenue"
                                    value={formatINR(totals.revenue)}
                                    accent={{ bg: "bg-violet-50", text: "text-violet-600" }}
                                />
                            </div>

                            {/* Charts */}
                            <div className="fd-charts-grid">
                                <div className="fd-chart-card">
                                    <div className="fd-chart-header">
                                        <p className="fd-chart-title">Daily bookings</p>
                                    </div>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={filtered} barGap={2}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} interval={Math.floor(filtered.length / 10)} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="active" name="Active" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="closed" name="Closed" fill="#10b981" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="cancelled" name="Cancelled" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="fd-chart-card">
                                    <p className="fd-chart-title">Status distribution</p>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                                                {pieData.map((entry) => (
                                                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="fd-pie-legend">
                                        {pieData.map((entry) => (
                                            <div key={entry.name} className="fd-pie-legend-item">
                                                <span className="fd-pie-legend-dot" style={{ backgroundColor: STATUS_COLORS[entry.name] }} />
                                                <span>{entry.name}</span>
                                                <span className="fd-pie-legend-value">
                                                    {entry.value} ({totals.total ? Math.round((entry.value / totals.total) * 100) : 0}%)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Revenue Trend */}
                            <div className="fd-chart-card full">
                                <div className="fd-chart-header">
                                    <p className="fd-chart-title">Revenue trend</p>
                                </div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={filtered}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} interval={Math.floor(filtered.length / 10)} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v/1000}k`} />
                                        <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                        <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#7c3aed" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Recent Orders */}
                        <div className="fd-recent-orders">
                            <div className="fd-section-header">
                                <h3>Recent Orders</h3>
                                <div className="fd-search-box">
                                    <Search className="h-4 w-4 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search orders..."
                                        value={searchOrder}
                                        onChange={(e) => setSearchOrder(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="fd-table-wrapper">
                                <table className="fd-table">
                                    <thead>
                                        <tr>
                                            <th>Order</th>
                                            <th>Buyer</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentOrders.map((order) => (
                                            <tr key={order.id}>
                                                <td>
                                                    <div className="fd-order-cell">
                                                        <span className="fd-order-name">{order.name}</span>
                                                        <span className="fd-order-id">{order.id}</span>
                                                    </div>
                                                </td>
                                                <td>{order.buyer}</td>
                                                <td className="fd-amount">{formatINR(order.amount)}</td>
                                                <td>
                                                    <span className={`fd-status-badge ${order.status.toLowerCase().replace(' ', '-')}`}>
                                                        {getStatusIcon(order.status)}
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td>{order.date}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Machines Status */}
                        <div className="fd-machines-status">
                            <div className="fd-section-header">
                                <h3>Machines</h3>
                                <span className="fd-machines-count">3 of 6 running</span>
                            </div>
                            <div className="fd-machines-grid">
                                {machines.map((machine) => (
                                    <div key={machine.id} className="fd-machine-card">
                                        <div className="fd-machine-header">
                                            <span className="fd-machine-id">{machine.id}</span>
                                            <span className={`fd-machine-status ${machine.status.toLowerCase()}`}>
                                                {machine.status}
                                            </span>
                                        </div>
                                        <p className="fd-machine-name">{machine.name}</p>
                                        <div className="fd-machine-utilization">
                                            <div className="fd-utilization-bar">
                                                <div 
                                                    className="fd-utilization-fill" 
                                                    style={{ width: `${machine.utilization}%`, backgroundColor: machine.utilization > 70 ? '#10b981' : machine.utilization > 30 ? '#4f46e5' : '#ef4444' }}
                                                ></div>
                                            </div>
                                            <span className="fd-utilization-text">{machine.utilization}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Chats */}
                        <div className="fd-chats">
                            <div className="fd-section-header">
                                <h3>Chats</h3>
                                <span className="fd-chats-unread">3 new</span>
                            </div>
                            <div className="fd-chats-list">
                                {chats.map((chat, index) => (
                                    <div key={index} className={`fd-chat-item ${chat.unread ? 'unread' : ''}`}>
                                        <div className="fd-chat-avatar">{chat.initial}</div>
                                        <div className="fd-chat-content">
                                            <div className="fd-chat-header">
                                                <span className="fd-chat-name">{chat.name}</span>
                                                <span className="fd-chat-time">{chat.time}</span>
                                            </div>
                                            <p className="fd-chat-message">{chat.message}</p>
                                        </div>
                                        {chat.unread && <div className="fd-chat-unread-dot"></div>}
                                    </div>
                                ))}
                            </div>
                            <button className="fd-chats-view-all">Open all chats →</button>
                        </div>
                    </>
                )}

                {/* ========== ORDERS TAB ========== */}
                {activeTab === 'orders' && (
                    <div className="fd-orders-tab">
                        <div className="fd-orders-stats">
                            <div className="fd-order-stat">
                                <span className="fd-order-stat-value">7</span>
                                <span className="fd-order-stat-label">Total Orders</span>
                            </div>
                            <div className="fd-order-stat">
                                <span className="fd-order-stat-value">1</span>
                                <span className="fd-order-stat-label">Pending Acceptance</span>
                            </div>
                            <div className="fd-order-stat">
                                <span className="fd-order-stat-value">3</span>
                                <span className="fd-order-stat-label">Accepted / Active</span>
                            </div>
                            <div className="fd-order-stat">
                                <span className="fd-order-stat-value">3</span>
                                <span className="fd-order-stat-label">Delivered</span>
                            </div>
                        </div>

                        <div className="fd-orders-filter">
                            <div className="fd-search-box">
                                <Search className="h-4 w-4 text-slate-400" />
                                <input type="text" placeholder="Search by order ID, item, vendor..." />
                            </div>
                            <div className="fd-order-tabs">
                                <button className="active">Active Orders (4)</button>
                                <button>Delivered Orders (3)</button>
                            </div>
                        </div>

                        <div className="fd-active-orders">
                            {activeOrders.map((order) => (
                                <div key={order.id} className="fd-active-order-card">
                                    <div className="fd-active-order-header">
                                        <div>
                                            <h4 className="fd-active-order-name">{order.name}</h4>
                                            <p className="fd-active-order-id">{order.id} · {order.category}</p>
                                        </div>
                                        <span className={`fd-status-badge ${order.status.toLowerCase().replace(/ /g, '-')}`}>
                                            {getStatusIcon(order.status)}
                                            {order.status}
                                        </span>
                                    </div>
                                    <div className="fd-active-order-details">
                                        <div className="fd-active-order-vendor">
                                            <span className="fd-vendor-name">{order.vendor}</span>
                                            <span className="fd-vendor-rating">
                                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                                {order.rating}
                                            </span>
                                            <span className="fd-vendor-location">{order.location}</span>
                                        </div>
                                        <div className="fd-active-order-meta">
                                            <span className="fd-order-amount">{formatINR(order.amount)}</span>
                                            <span className="fd-order-units">{order.units}</span>
                                            <span className="fd-order-delivery">
                                                <Calendar className="h-3 w-3" />
                                                {order.delivery}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="fd-active-order-actions">
                                        {order.status === 'Awaiting Your Acceptance' && (
                                            <button className="fd-btn-primary">Accept & Place Order</button>
                                        )}
                                        <button className="fd-btn-secondary">View Details</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ========== MACHINES TAB ========== */}
                {activeTab === 'machines' && (
                    <div className="fd-machines-tab">
                        <div className="fd-machines-toolbar">
                            <div className="fd-search-box">
                                <Search className="h-4 w-4 text-slate-400" />
                                <input 
                                    type="text" 
                                    placeholder="Search by name, ID, make..." 
                                    value={searchMachine}
                                    onChange={(e) => setSearchMachine(e.target.value)}
                                />
                            </div>
                            <div className="fd-machine-filters">
                                <select className="fd-filter-select">
                                    <option>All categories</option>
                                    <option>3D Printing</option>
                                    <option>CNC</option>
                                    <option>Laser Cutting</option>
                                    <option>PCB</option>
                                </select>
                                <button className="fd-btn-secondary">Reset</button>
                                <button className="fd-btn-primary">
                                    <Plus className="h-4 w-4" />
                                    Add Machine
                                </button>
                            </div>
                        </div>

                        <div className="fd-table-wrapper">
                            <table className="fd-table">
                                <thead>
                                    <tr>
                                        <th>Machine</th>
                                        <th>Category</th>
                                        <th>Centre</th>
                                        <th>Make</th>
                                        <th>Rate</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {machineCatalog.map((machine) => (
                                        <tr key={machine.id}>
                                            <td>
                                                <div className="fd-order-cell">
                                                    <span className="fd-order-name">{machine.name}</span>
                                                    <span className="fd-order-id">{machine.id}</span>
                                                </div>
                                            </td>
                                            <td>{machine.category}</td>
                                            <td>{machine.centre}</td>
                                            <td>{machine.make}</td>
                                            <td className="fd-amount">{formatINR(machine.rate)}/hr</td>
                                            <td>
                                                <span className={`fd-status-badge ${machine.status.toLowerCase()}`}>
                                                    <span className="fd-status-dot" style={{ backgroundColor: getMachineStatusColor(machine.status) }}></span>
                                                    {machine.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="fd-action-buttons">
                                                    <button className="fd-action-btn"><Eye className="h-4 w-4" /></button>
                                                    <button className="fd-action-btn"><Edit className="h-4 w-4" /></button>
                                                    <button className="fd-action-btn"><Trash2 className="h-4 w-4" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ========== PIPELINE TAB ========== */}
                {activeTab === 'pipeline' && (
                    <div className="fd-pipeline-tab">
                        <div className="fd-pipeline-stats">
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Sourcing</span>
                                <span className="fd-pipeline-stat-value">4 · {formatINR(1195000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Enquiry</span>
                                <span className="fd-pipeline-stat-value">2 · {formatINR(275000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Quote</span>
                                <span className="fd-pipeline-stat-value">1 · {formatINR(920000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Approval</span>
                                <span className="fd-pipeline-stat-value">1 · {formatINR(920000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">PO</span>
                                <span className="fd-pipeline-stat-value">1 · {formatINR(920000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Execution</span>
                                <span className="fd-pipeline-stat-value">1 · {formatINR(920000)}</span>
                            </div>
                            <div className="fd-pipeline-arrow">→</div>
                            <div className="fd-pipeline-stat">
                                <span className="fd-pipeline-stat-label">Payment</span>
                                <span className="fd-pipeline-stat-value">2 · {formatINR(920000)}</span>
                            </div>
                        </div>

                        <div className="fd-pipeline-grid">
                            <div className="fd-pipeline-card">
                                <h4>Enquiry status</h4>
                                <div className="fd-pipeline-status-list">
                                    <div className="fd-pipeline-status-item">
                                        <span>Pending Quote</span>
                                        <span>1 · ₹0</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Quote Submitted</span>
                                        <span>1 · {formatINR(275000)}</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Approved</span>
                                        <span>1 · {formatINR(920000)}</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Rejected</span>
                                        <span>0 · ₹0</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Revision</span>
                                        <span>0 · ₹0</span>
                                    </div>
                                </div>
                            </div>
                            <div className="fd-pipeline-card">
                                <h4>Project status</h4>
                                <div className="fd-pipeline-status-list">
                                    <div className="fd-pipeline-status-item">
                                        <span>Initiated</span>
                                        <span>0 · ₹0</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>In Progress</span>
                                        <span>2 · {formatINR(1840000)}</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Completed</span>
                                        <span>1 · {formatINR(920000)}</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>On Hold</span>
                                        <span>0 · ₹0</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Cancelled</span>
                                        <span>0 · ₹0</span>
                                    </div>
                                </div>
                            </div>
                            <div className="fd-pipeline-card">
                                <h4>Pending Items</h4>
                                <div className="fd-pipeline-status-list">
                                    <div className="fd-pipeline-status-item">
                                        <span>Total Pending</span>
                                        <span>3 · {formatINR(535000)}</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Quotes</span>
                                        <span>1</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Approvals</span>
                                        <span>1</span>
                                    </div>
                                    <div className="fd-pipeline-status-item">
                                        <span>Orders</span>
                                        <span>1</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========== SUPPORT TAB ========== */}
                {activeTab === 'support' && (
                    <div className="fd-support-tab">
                        <div className="fd-support-header">
                            <div className="fd-support-title">
                                <h2>Support Centre</h2>
                                <p>How can we help?</p>
                            </div>
                            <p className="fd-support-desc">
                                Search our FAQs or reach out directly — our team typically responds within 24 hours.
                            </p>
                        </div>

                        <div className="fd-support-search">
                            <Search className="h-5 w-5 text-slate-400" />
                            <input type="text" placeholder="Search for answers..." />
                        </div>

                        <div className="fd-support-channels">
                            <div className="fd-support-channel">
                                <Mail className="h-6 w-6 text-indigo-600" />
                                <h4>Email us</h4>
                                <p>support@apic.ap.gov.in</p>
                            </div>
                            <div className="fd-support-channel">
                                <Phone className="h-6 w-6 text-indigo-600" />
                                <h4>Call us</h4>
                                <p>+91 891 123 4567</p>
                            </div>
                            <div className="fd-support-channel">
                                <MessageCircle className="h-6 w-6 text-indigo-600" />
                                <h4>Live chat</h4>
                                <p>Mon-Sat, 9am-6pm IST</p>
                            </div>
                        </div>

                        <div className="fd-support-faq">
                            <h3>Frequently Asked Questions</h3>
                            <div className="fd-faq-list">
                                <div className="fd-faq-item">
                                    <div className="fd-faq-question">
                                        <span>How long does a typical prototype take to build?</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </div>
                                </div>
                                <div className="fd-faq-item">
                                    <div className="fd-faq-question">
                                        <span>How is the AI cost estimate calculated?</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </div>
                                </div>
                                <div className="fd-faq-item">
                                    <div className="fd-faq-question">
                                        <span>Can I change the manufacturing centre after submitting a request?</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </div>
                                </div>
                                <div className="fd-faq-item">
                                    <div className="fd-faq-question">
                                        <span>What file formats are supported for CAD upload?</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </div>
                                </div>
                                <div className="fd-faq-item">
                                    <div className="fd-faq-question">
                                        <span>How do I pay for an approved quotation?</span>
                                        <ChevronRight className="h-4 w-4" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="fd-support-ticket">
                            <h3>Can't find what you're looking for? Send us a message.</h3>
                            <div className="fd-ticket-form">
                                <div className="fd-form-group">
                                    <label>Subject</label>
                                    <input type="text" placeholder="e.g. Delay in EV Battery Enclosure v2" />
                                </div>
                                <div className="fd-form-group">
                                    <label>Message</label>
                                    <textarea rows="4" placeholder="Describe your issue..."></textarea>
                                </div>
                                <button className="fd-btn-primary">Submit Ticket</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}